/**
 * @file useSchedule.js
 *
 * Schedule Hook
 *
 * Manages a professional's weekly availability slots.
 * Each slot = one day + time range (e.g., Monday 09:00–17:00).
 *
 * Note the different update strategies:
 *   createSlot/updateSlot → re-fetch the entire list (simpler, ensures consistency)
 *   deleteSlot → optimistic update with .filter() (faster UX, no extra API call)
 */
import { useState, useEffect, useCallback } from "react";
import { toast } from "react-toastify";
import { scheduleService } from "../services";
import { SCHEDULE_STRINGS } from "../constants/strings";

/**
 * Hook for managing a professional's weekly availability slots (CRUD).
 *
 * @returns {{schedules: Array, isLoading: boolean, error: Error|null, refresh: Function, createSlot: Function, updateSlot: Function, deleteSlot: Function}}
 */
export function useSchedule() {
  const [schedules, setSchedules] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchSchedules = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);
      const response = await scheduleService.getMe();
      setSchedules(response.data || []);
    } catch (err) {
      setError(err);
    } finally {
      setIsLoading(false);
    }
  }, []);

  // After creating a slot, re-fetch the full list to stay in sync
  const createSlot = async (data) => {
    const response = await scheduleService.create(data);
    await fetchSchedules();
    toast.success(SCHEDULE_STRINGS.CREATED);
    return response.data;
  };

  const updateSlot = async (id, data) => {
    const response = await scheduleService.update(id, data);
    await fetchSchedules();
    toast.success(SCHEDULE_STRINGS.UPDATED);
    return response.data;
  };

  // Optimistic delete: remove from local state immediately (no re-fetch needed)
  const deleteSlot = async (id) => {
    await scheduleService.remove(id);
    setSchedules((prev) => prev.filter((s) => s.id !== id));
    toast.success(SCHEDULE_STRINGS.DELETED);
  };

  useEffect(() => {
    fetchSchedules();
  }, [fetchSchedules]);

  return {
    schedules,
    isLoading,
    error,
    refresh: fetchSchedules,
    createSlot,
    updateSlot,
    deleteSlot,
  };
}
